import { debounce, keyBy } from 'lodash'
import React, { useContext, useEffect, useMemo, useState } from 'react'

import { LocaleContext } from '../locale/i18n'
import {
  DeviceType,
  IndustryOrMaterial,
  Standard,
  TypeOfTesting,
} from '../logic/catalog'
import {
  DeviceFilter,
  SelectInputHandler,
  ProductFiltersByType,
  IndustryOrMaterialFilter,
  TypeOfTestingFilter,
  StandardFilter,
} from '../logic/productFiltering'
import { ProductFilterType } from '../logic/shared-types'
import { standardToString } from '../logic/standard'

import Select from './Select'
import './ProductFilters.scss'

export interface ProductFiltersProps {
  availableFilters: {
    [ProductFilterType.INDUSTRY_OR_MATERIAL]: IndustryOrMaterial[]
    [ProductFilterType.TYPE_OF_TESTING]: TypeOfTesting[]
    [ProductFilterType.DEVICE_TYPE]: string[]
    [ProductFilterType.STANDARD]: Standard[]
  }
  deviceTypes: DeviceType[]
  filters: ProductFiltersByType
  onChange: {
    [ProductFilterType.INDUSTRY_OR_MATERIAL]: SelectInputHandler<IndustryOrMaterialFilter>
    [ProductFilterType.TYPE_OF_TESTING]: SelectInputHandler<TypeOfTestingFilter>
    [ProductFilterType.DEVICE_TYPE]: SelectInputHandler<DeviceFilter>
    [ProductFilterType.STANDARD]: SelectInputHandler<StandardFilter>
    [ProductFilterType.TEXT]: (newValue: string) => void
  }
}

const ProductFilters = ({
  availableFilters: { deviceType, industryOrMaterial, standard, typeOfTesting },
  deviceTypes,
  filters,
  onChange,
}: ProductFiltersProps) => {
  const { t, collator } = useContext(LocaleContext)
  const deviceTypesById = keyBy(deviceTypes, 'deviceTypeId')

  const handleTextChange = useMemo(
    () => debounce(onChange.text, 200),
    [onChange.text],
  )
  const [textQuery, setTextQuery] = useState(filters.text.value)
  const [queryLoaded, setQueryLoaded] = useState(false)
  useEffect(() => {
    if (filters.text.value !== textQuery) {
      handleTextChange(textQuery)
    }
  }, [textQuery])

  // populate textQuery after query is loaded from url params only once
  useEffect(() => {
    if (filters.text.value !== '' && !queryLoaded) {
      setTextQuery(filters.text.value)
      setQueryLoaded(true)
    }
  }, [filters.text.value])

  const deviceOptions = deviceType.map(value => {
    const dt = deviceTypesById[value]
    return {
      label:
        dt?.nameLong && dt.nameLong === dt?.nameShort
          ? dt.nameLong
          : [
              deviceTypesById[value]?.nameLong ?? '',
              deviceTypesById[value]?.nameShort ?? '',
            ].join(' '),
      value,
    }
  })
  const standardOptions = standard.map(value => ({
    label: standardToString(value),
    value: value.slug,
  }))

  return (
    <div className="productFilters">
      <input
        className="productFilters__textInput inputField"
        // onInput instead of onChange to support native input[type=search] clear when input not rendered on client
        onInput={e => setTextQuery(e.currentTarget.value)}
        type="search"
        value={textQuery}
        id="text-query"
        placeholder={t('productFilters.textSearch')}
      />
      <div className="productFilters__row">
        <Select
          data-test-id="select-device-type"
          onChange={option =>
            onChange[ProductFilterType.DEVICE_TYPE](option?.value ?? null)
          }
          options={deviceOptions.sort((a, b) =>
            collator.compare(a.label, b.label),
          )}
          renderOption={({ label, value }) => {
            const validDevice = deviceTypesById[value]
            return validDevice?.nameLong &&
              validDevice.nameShort &&
              validDevice.nameLong !== validDevice.nameShort ? (
              <span>
                {validDevice.nameLong}
                <strong className="select__optionSubText">
                  &nbsp;({validDevice.nameShort})
                </strong>
              </span>
            ) : (
              label
            )
          }}
          value={deviceOptions.find(
            ({ value }) => filters.deviceType.value === value,
          )}
          noOptionsMessage={t('productFilters.deviceType.none')}
          placeholder={t('productFilters.deviceType.label')}
        />
        <Select
          data-test-id="select-standard"
          onChange={option =>
            onChange[ProductFilterType.STANDARD](option?.value ?? null)
          }
          options={standardOptions.sort((a, b) =>
            collator.compare(a.label, b.label),
          )}
          value={standardOptions.find(
            ({ value }) => filters.standard.value === value,
          )}
          noOptionsMessage={t('productFilters.standard.none')}
          placeholder={t('productFilters.standard.label')}
        />
        <Select
          data-test-id="select-type-of-testing"
          onChange={option =>
            onChange[ProductFilterType.TYPE_OF_TESTING](option?.value ?? null)
          }
          options={typeOfTesting
            .map(value => ({
              label: t(`typeOfTesting.${value}`),
              value,
            }))
            .sort((a, b) => collator.compare(a.label, b.label))}
          value={
            filters.typeOfTesting.value
              ? {
                  label: t(`typeOfTesting.${filters.typeOfTesting.value}`),
                  value: filters.typeOfTesting.value,
                }
              : undefined
          }
          renderOption={({ label, value }) => {
            const validTypeOfTesting = typeOfTesting.find(tt => tt === value)
            return validTypeOfTesting ? (
              <>
                <img
                  className="h-5 w-5"
                  src={`/category-icons/${validTypeOfTesting}.svg`}
                  alt={validTypeOfTesting}
                />
                <span>{label}</span>
              </>
            ) : (
              label
            )
          }}
          noOptionsMessage={t('productFilters.typeOfTesting.none')}
          placeholder={t('productFilters.typeOfTesting.label')}
        />
        <Select
          data-test-id="select-industry-or-material"
          onChange={option =>
            onChange[ProductFilterType.INDUSTRY_OR_MATERIAL](
              option?.value ?? null,
            )
          }
          options={industryOrMaterial
            .map(value => ({
              label: t(`industryOrMaterial.${value}`),
              value,
            }))
            .sort((a, b) => collator.compare(a.label, b.label))}
          renderOption={({ label, value }) => {
            const validIndustryOrMaterial = industryOrMaterial.find(
              tt => tt === value,
            )
            return validIndustryOrMaterial ? (
              <>
                <img
                  className="h-5 w-5"
                  src={`/category-icons/${validIndustryOrMaterial}.svg`}
                  alt={validIndustryOrMaterial}
                />
                <p>{label}</p>
              </>
            ) : (
              label
            )
          }}
          value={
            filters.industryOrMaterial.value
              ? {
                  label: t(
                    `industryOrMaterial.${filters.industryOrMaterial.value}`,
                  ),
                  value: filters.industryOrMaterial.value,
                }
              : undefined
          }
          noOptionsMessage={t('productFilters.industryOrMaterial.none')}
          placeholder={t('productFilters.industryOrMaterial.label')}
        />
      </div>
    </div>
  )
}

export default ProductFilters
